const hasNonEmptyString = (value: unknown) =>
  typeof value === "string" && value.trim().length > 0;

export const hasMandatoryOnboardingFields = (userDoc: any) => {
  if (!userDoc) return false;

  return (
    hasNonEmptyString(userDoc.firstName) &&
    hasNonEmptyString(userDoc.lastName) &&
    hasNonEmptyString(userDoc.username)
  );
};

/**
 * Safely get a preference value that might be in a nested object or a flat dotted key.
 */
const getPref = (userDoc: any, prefKey: string) => {
  // Try nested first: onboardingPrefs.someKey
  if (userDoc?.onboardingPrefs && typeof userDoc.onboardingPrefs === 'object') {
    if (userDoc.onboardingPrefs[prefKey] !== undefined) {
      return userDoc.onboardingPrefs[prefKey];
    }
  }
  // Fallback to flat key: "onboardingPrefs.someKey"
  return userDoc?.[`onboardingPrefs.${prefKey}`];
};

export const hasCompletedInitialOnboardingTour = (userDoc: any) => {
  const explicitCompletion = getPref(userDoc, 'initialOnboardingCompleted');

  if (explicitCompletion === true) {
    return true;
  }

  if (explicitCompletion === false) {
    return false;
  }

  return hasMandatoryOnboardingFields(userDoc);
};

export const hasCompletedPlateTour = (userDoc: any) => {
  return getPref(userDoc, 'plateTourCompleted') === true;
};

export const hasCompletedPhoneTour = (userDoc: any) => {
  return getPref(userDoc, 'phoneTourCompleted') === true;
};

export const isPhoneOnlyUser = (userDoc: any) => {
  const providers = userDoc?.providers;
  return (
    Array.isArray(providers) &&
    providers.length === 1 &&
    providers[0] === "phone"
  );
};

/**
 * Derive phone-only signup from the Firebase auth user. Used alongside
 * isPhoneOnlyUser to detect the case where the Firestore doc hasn't caught
 * up yet (fresh signup) or providerData wasn't captured into `providers`.
 */
export const isPhoneOnlyAuthUser = (authUser: any) => {
  if (!authUser) return false;
  return !!authUser.phoneNumber && !authUser.email;
};

export const hasFollowedPlate = (userDoc: any) => {
  const plates = userDoc?.followedPlates;
  return Array.isArray(plates) && plates.length > 0;
};

/**
 * The post-onboarding sidebar plate-tour prompt is shown only to users who:
 *  - aren't phone-only (those completed an in-onboarding plate step),
 *  - don't already follow a plate,
 *  - haven't dismissed/completed the tour previously.
 */
export const shouldShowPlateGuidePrompt = (userDoc: any) => {
  return (
    !isPhoneOnlyUser(userDoc) &&
    !hasFollowedPlate(userDoc) &&
    !hasCompletedPlateTour(userDoc)
  );
};
